import { Box, Button, Checkbox, FormControlLabel, TextField, Typography } from '@mui/material'
import translate from '../../../i18n/translate'
import toast from 'react-hot-toast'
import { useIntl } from 'react-intl'
import { Formik, Form } from 'formik'
import { FIELDS, useValidationSchema } from './useValidationSchema'
import { organizerApi } from '../../../modules/services/organizerService'
import { useDebounce } from 'react-use'
import { useState } from 'react'

type FormViewProps = {
  userId: number;
  event: any
  billAccount: any
  onCloseModal: () => void
}

export default function FormView({
  userId,
  event,
  billAccount,
  onCloseModal,
}: FormViewProps) {
  const intl = useIntl()

  const [vals, setVals] =  useState<any>({})
  const [isOverdraftConfirmed, setIsOverdraftConfirmed] =  useState<boolean>(false)
  const [isOtherRecipient, setIsOtherRecipient] =  useState<boolean>(false)

  const [
    createOrganizerEventTransaction,
    { isLoading }
  ] = organizerApi.useCreateOrganizerEventTransactionMutation()

  const [
    calculateOrganizerEventTransaction,
    { isLoading: isCalculating, data: calculateData }
  ] = organizerApi.useCalculateOrganizerEventTransactionMutation()

  const { data: organizerEventData } = organizerApi.useGetOrganizerEventInfoQuery({ userId, eventLinkId: event?.eventLink }, { skip: !event });

  const handleSubmit = async (values: any) => {
    try {
      if (userId) {
        let data: any = {
          amount: values?.[FIELDS.AMOUNT] || null,
          feeAmount: values?.[FIELDS.FEE_AMOUNT] || null,
          fastWithdrawalFeeAmount: values?.[FIELDS.FAST_WITHDRAWAL_FEE_AMOUNT] || null,
          adminNote: values?.[FIELDS.ADMIN_NOTE] || null,
          billAccountId: billAccount?.id || null,
        }

        if (values?.[FIELDS.IS_OTHER_RECIPIENT]) {
          data = {
            ...data,
            recipientInfo: {
              fullName: values?.[FIELDS.RECIPIENT_FULL_NAME] || null,
              recipientId: values?.[FIELDS.RECIPIENT_ID] || null,
              phone: values?.[FIELDS.RECIPIENT_PHONE] || null,
              email: values?.[FIELDS.RECIPIENT_EMAIL] || null,
            }
          }
        }

        await createOrganizerEventTransaction({
          userId: userId,
          eventLinkId: event.eventLink,
          data
        });

      }
      onCloseModal()
      toast.success(translate('successTransactionCreateMessage'))
    } catch (error) {
      toast.error(translate('errorGenericMessage'))
    }
  }

  useDebounce(
    async () => {
      if (vals?.[FIELDS.FEE_AMOUNT] || vals?.[FIELDS.FAST_WITHDRAWAL_FEE_AMOUNT] || vals?.[FIELDS.AMOUNT]) {
        await calculateOrganizerEventTransaction({
          userId: userId,
          eventLinkId: event.eventLink,
          data: {
            amount: vals?.[FIELDS.AMOUNT] || null,
            feeAmount: vals?.[FIELDS.FEE_AMOUNT] || null,
            fastWithdrawalFeeAmount: vals?.[FIELDS.FAST_WITHDRAWAL_FEE_AMOUNT] || null,
          }
        });
      }
      setIsOverdraftConfirmed(false)
    },
    750,
    [vals?.[FIELDS.FEE_AMOUNT], vals?.[FIELDS.FAST_WITHDRAWAL_FEE_AMOUNT], vals?.[FIELDS.AMOUNT]]
  );

  const validationSchema = useValidationSchema()

  return (
    <Formik
      initialValues={{
        [FIELDS.AMOUNT]: null,
        [FIELDS.FEE_AMOUNT]: null,
        [FIELDS.FAST_WITHDRAWAL_FEE_AMOUNT]: null,
        [FIELDS.ADMIN_NOTE]: null,
        [FIELDS.IS_OTHER_RECIPIENT]: null,
        [FIELDS.RECIPIENT_FULL_NAME]: null,
        [FIELDS.RECIPIENT_ID]: null,
        [FIELDS.RECIPIENT_PHONE]: null,
        [FIELDS.RECIPIENT_EMAIL]: null,
      }}
      onSubmit={handleSubmit}
      enableReinitialize
      validationSchema={validationSchema}
    >
      {({ errors, values, touched, handleBlur, setFieldValue, handleChange, isValid }) => {
        const onChange = (e: any) => {
          handleChange(e);
          setVals({ ...vals, [e.target.name]: e.target.value ? Number(e.target.value) : null });
        }
        return (
          <Form>
            <Box
              p={2}
              display='flex'
              flexDirection='column'
              justifyContent='center'
              width={1}
              alignItems='center'
              gap={2}
            >
              <Box>
                <Typography variant='h6'>
                  {intl.formatMessage({ id: 'selectedEventName' })}: {event?.name}
                </Typography>
                {organizerEventData ? (
                  <Box>
                    <Typography>
                      {intl.formatMessage({ id: 'totalEventAmount' })}: {intl.formatNumber(organizerEventData?.totalEventAmount)}₪
                    </Typography>
                    <Typography>
                      {intl.formatMessage({ id: 'totalWithdrawnAmount' })}: {intl.formatNumber(organizerEventData?.totalWithdrawnAmount)}₪
                    </Typography>
                    <Typography>
                      {intl.formatMessage({ id: 'availableForWithdrawalAmount' })}: {intl.formatNumber(organizerEventData?.availableForWithdrawalAmount)}₪
                    </Typography>
                  </Box>
                ) : null}
              </Box>
              <TextField
                name={FIELDS.AMOUNT}
                value={values?.[FIELDS.AMOUNT]}
                error={touched?.[FIELDS.AMOUNT] && Boolean(errors?.[FIELDS.AMOUNT])}
                helperText={touched?.[FIELDS.AMOUNT] && errors?.[FIELDS.AMOUNT]}
                onChange={onChange}
                onBlur={handleBlur}
                variant='outlined'
                color='warning'
                disabled={isLoading}
                fullWidth
                type='number'
                label={intl.formatMessage({ id: 'transactionAmountLabel' })}
                placeholder={intl.formatMessage({
                  id: 'transactionAmountLabel'
                })}
                dir='ltr'
                sx={{
                  '.MuiInputBase-root.MuiInput-root:before': {
                    borderBottom: '0px'
                  },
                  '.MuiInputBase-root.MuiInput-root:hover': {
                    borderBottom: '0px'
                  }
                }}
              />
              <TextField
                name={FIELDS.FEE_AMOUNT}
                value={values?.[FIELDS.FEE_AMOUNT]}
                error={touched?.[FIELDS.FEE_AMOUNT] && Boolean(errors?.[FIELDS.FEE_AMOUNT])}
                helperText={touched?.[FIELDS.FEE_AMOUNT] && errors?.[FIELDS.FEE_AMOUNT]}
                onChange={onChange}
                onBlur={handleBlur}
                variant='outlined'
                color='warning'
                disabled={isLoading}
                fullWidth
                type='number'
                label={intl.formatMessage({ id: 'transactionFeeAmountLabel' })}
                placeholder={intl.formatMessage({
                  id: 'transactionFeeAmountLabel'
                })}
                dir='ltr'
                sx={{
                  '.MuiInputBase-root.MuiInput-root:before': {
                    borderBottom: '0px'
                  },
                  '.MuiInputBase-root.MuiInput-root:hover': {
                    borderBottom: '0px'
                  }
                }}
              />
              <TextField
                name={FIELDS.FAST_WITHDRAWAL_FEE_AMOUNT}
                value={values?.[FIELDS.FAST_WITHDRAWAL_FEE_AMOUNT]}
                error={touched?.[FIELDS.FAST_WITHDRAWAL_FEE_AMOUNT] && Boolean(errors?.[FIELDS.FAST_WITHDRAWAL_FEE_AMOUNT])}
                helperText={touched?.[FIELDS.FAST_WITHDRAWAL_FEE_AMOUNT] && errors?.[FIELDS.FAST_WITHDRAWAL_FEE_AMOUNT]}
                onChange={onChange}
                onBlur={handleBlur}
                variant='outlined'
                color='warning'
                disabled={isLoading}
                fullWidth
                type='number'
                label={intl.formatMessage({ id: 'transactionFastWithdrawalFeeAmountLabel' })}
                placeholder={intl.formatMessage({
                  id: 'transactionFastWithdrawalFeeAmountLabel'
                })}
                dir='ltr'
                sx={{
                  '.MuiInputBase-root.MuiInput-root:before': {
                    borderBottom: '0px'
                  },
                  '.MuiInputBase-root.MuiInput-root:hover': {
                    borderBottom: '0px'
                  }
                }}
              />
              <TextField
                name={FIELDS.ADMIN_NOTE}
                value={values?.[FIELDS.ADMIN_NOTE]}
                error={touched?.[FIELDS.ADMIN_NOTE] && Boolean(errors?.[FIELDS.ADMIN_NOTE])}
                helperText={touched?.[FIELDS.ADMIN_NOTE] && errors?.[FIELDS.ADMIN_NOTE]}
                onChange={handleChange}
                onBlur={handleBlur}
                variant='outlined'
                color='warning'
                disabled={isLoading}
                fullWidth
                type='text'
                label={intl.formatMessage({
                  id: 'transactionAdminNoteLabel'
                })}
                placeholder={intl.formatMessage({
                  id: 'transactionAdminNoteLabel'
                })}
                dir='ltr'
                sx={{
                  '.MuiInputBase-root.MuiInput-root:before': {
                    borderBottom: '0px'
                  },
                  '.MuiInputBase-root.MuiInput-root:hover': {
                    borderBottom: '0px'
                  }
                }}
              />
              {calculateData?.totalTransactionAmount ? (
                <Box border='1px solid #ed6c02' borderRadius='8px' p={2} width={1}>
                  <Typography>
                    {intl.formatMessage({ id: 'totalTransactionAmount' })}: <b>{intl.formatNumber(calculateData?.totalTransactionAmount)}₪</b>
                  </Typography>
                  {calculateData?.amount ? (
                    <Typography>
                      {intl.formatMessage({ id: 'transactionAmount' })}: {intl.formatNumber(calculateData?.amount)}₪
                    </Typography>
                  ) : null}
                  {calculateData?.feeAmount ? (
                    <Typography>
                      {intl.formatMessage({ id: 'transactionFeeAmount' })}: {intl.formatNumber(calculateData?.feeAmount)}₪
                    </Typography>
                  ) : null}
                  {calculateData?.fastWithdrawalFeeAmount ? (
                    <Typography>
                      {intl.formatMessage({ id: 'fastWithdrawalFeeAmount' })}: {intl.formatNumber(calculateData?.fastWithdrawalFeeAmount)}₪
                    </Typography>
                  ) : null}
                  {calculateData?.isOverdraft ? (
                    <>
                      <Typography color='error' fontWeight='bold'>
                        {intl.formatMessage({ id: 'warningOverdraftAmount' })}
                      </Typography>
                      <Typography fontWeight='bold'>
                        {intl.formatMessage({ id: 'overdraftAfterTransaction' })}: -{intl.formatNumber(calculateData?.overdraftAmount)}₪
                      </Typography>
                    </>
                  ) : null}
                </Box>
              ) : null}
              <Box display='flex' justifyContent='flex-start' width={1}>
                <FormControlLabel 
                  control={<Checkbox checked={isOtherRecipient} color='warning' />} 
                  label={intl.formatMessage({ id: 'otherRecipientLabel' })} 
                  onChange={async(e, checked) => {
                    setIsOtherRecipient(checked);
                    await setFieldValue(FIELDS.IS_OTHER_RECIPIENT, checked);
                  }} 
                />
              </Box>
              {isOtherRecipient ? (
                <Box display='flex' flexDirection='column' gap={2} width={1}>
                  <TextField
                    name={FIELDS.RECIPIENT_FULL_NAME}
                    value={values?.[FIELDS.RECIPIENT_FULL_NAME]}
                    error={touched?.[FIELDS.RECIPIENT_FULL_NAME] && Boolean(errors?.[FIELDS.RECIPIENT_FULL_NAME])}
                    helperText={touched?.[FIELDS.RECIPIENT_FULL_NAME] && errors?.[FIELDS.RECIPIENT_FULL_NAME]}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    variant='outlined'
                    color='warning'
                    disabled={isLoading}
                    fullWidth
                    type='text'
                    label={intl.formatMessage({
                      id: 'recipientFullNameLabel'
                    })}
                    placeholder={intl.formatMessage({
                      id: 'recipientFullNameLabel'
                    })}
                    dir='ltr'
                    sx={{
                      '.MuiInputBase-root.MuiInput-root:before': {
                        borderBottom: '0px'
                      },
                      '.MuiInputBase-root.MuiInput-root:hover': {
                        borderBottom: '0px'
                      }
                    }}
                  />
                  <TextField
                    name={FIELDS.RECIPIENT_ID}
                    value={values?.[FIELDS.RECIPIENT_ID]}
                    error={touched?.[FIELDS.RECIPIENT_ID] && Boolean(errors?.[FIELDS.RECIPIENT_ID])}
                    helperText={touched?.[FIELDS.RECIPIENT_ID] && errors?.[FIELDS.RECIPIENT_ID]}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    variant='outlined'
                    color='warning'
                    disabled={isLoading}
                    fullWidth
                    type='text'
                    label={intl.formatMessage({
                      id: 'recipientIdLabel'
                    })}
                    placeholder={intl.formatMessage({
                      id: 'recipientIdLabel'
                    })}
                    dir='ltr'
                    sx={{
                      '.MuiInputBase-root.MuiInput-root:before': {
                        borderBottom: '0px'
                      },
                      '.MuiInputBase-root.MuiInput-root:hover': {
                        borderBottom: '0px'
                      }
                    }}
                  />
                  <TextField
                    name={FIELDS.RECIPIENT_PHONE}
                    value={values?.[FIELDS.RECIPIENT_PHONE]}
                    error={touched?.[FIELDS.RECIPIENT_PHONE] && Boolean(errors?.[FIELDS.RECIPIENT_PHONE])}
                    helperText={touched?.[FIELDS.RECIPIENT_PHONE] && errors?.[FIELDS.RECIPIENT_PHONE]}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    variant='outlined'
                    color='warning'
                    disabled={isLoading}
                    fullWidth
                    type='tel'
                    label={intl.formatMessage({
                      id: 'recipientPhoneLabel'
                    })}
                    placeholder={intl.formatMessage({
                      id: 'recipientPhoneLabel'
                    })}
                    dir='ltr'
                    sx={{
                      '.MuiInputBase-root.MuiInput-root:before': {
                        borderBottom: '0px'
                      },
                      '.MuiInputBase-root.MuiInput-root:hover': {
                        borderBottom: '0px'
                      }
                    }}
                  />
                  <TextField
                    name={FIELDS.RECIPIENT_EMAIL}
                    value={values?.[FIELDS.RECIPIENT_EMAIL]}
                    error={touched?.[FIELDS.RECIPIENT_EMAIL] && Boolean(errors?.[FIELDS.RECIPIENT_EMAIL])}
                    helperText={touched?.[FIELDS.RECIPIENT_EMAIL] && errors?.[FIELDS.RECIPIENT_EMAIL]}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    variant='outlined'
                    color='warning'
                    disabled={isLoading}
                    fullWidth
                    type='email'
                    label={intl.formatMessage({
                      id: 'recipientEmailLabel'
                    })}
                    placeholder={intl.formatMessage({
                      id: 'recipientEmailLabel'
                    })}
                    dir='ltr'
                    sx={{
                      '.MuiInputBase-root.MuiInput-root:before': {
                        borderBottom: '0px'
                      },
                      '.MuiInputBase-root.MuiInput-root:hover': {
                        borderBottom: '0px'
                      }
                    }}
                  />
                </Box>
              ) : null}
              {calculateData?.isOverdraft ? (
                <FormControlLabel control={<Checkbox checked={isOverdraftConfirmed} color='warning' />} label={intl.formatMessage({ id: 'overdraftConfirmation' })} onChange={() => setIsOverdraftConfirmed((prev) => !prev)} />
              ) : null}
              <Button
                size='large'
                variant='contained'
                color='warning'
                type='submit'
                fullWidth
                disabled={isCalculating || isLoading || !isValid || !values?.amount || (calculateData?.isOverdraft && !isOverdraftConfirmed)}
              >
                {translate('formViewCreateButton')}
              </Button>
            </Box>
          </Form>
        )
      }}
    </Formik>
  )
}
